// ============================================
// Order Pipeline Tab
// Funnel view of order statuses with proportional bars
// ============================================

import type { PipelineEntry } from './useFinancialData';
import type { OrderStatus } from '../../../types/order';

interface OrderPipelineTabProps {
  pipeline: PipelineEntry[];
  totalOrders: number;
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-text-muted/30',
  paid: 'bg-gold',
  processing: 'bg-suboptimal',
  shipped: 'bg-optimal',
  delivered: 'bg-accent-light',
  cancelled: 'bg-critical',
};

const STATUS_TEXT_COLORS: Record<OrderStatus, string> = {
  pending: 'text-text-muted',
  paid: 'text-gold',
  processing: 'text-suboptimal',
  shipped: 'text-optimal',
  delivered: 'text-accent-light',
  cancelled: 'text-critical',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export function OrderPipelineTab({
  pipeline,
  totalOrders,
}: OrderPipelineTabProps) {
  const maxCount = Math.max(...pipeline.map((p) => p.count), 1);

  return (
    <div
      className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-display text-text-primary">
          Order Pipeline
        </h3>
        <span className="text-base text-text-muted">
          {totalOrders} total order{totalOrders !== 1 ? 's' : ''}
        </span>
      </div>

      {totalOrders === 0 ? (
        <p className="text-text-muted text-base py-6 text-center">
          No orders in pipeline.
        </p>
      ) : (
        <div className="space-y-3">
          {pipeline.map((entry, i) => {
            const barPct = (entry.count / maxCount) * 100;
            const prevEntry = i > 0 ? pipeline[i - 1] : null;

            // Calculate drop-off from previous stage (skip for first and cancelled)
            let dropOff: number | null = null;
            if (
              prevEntry &&
              prevEntry.count > 0 &&
              entry.status !== 'cancelled'
            ) {
              dropOff = prevEntry.count - entry.count;
            }

            return (
              <div key={entry.status}>
                {/* Drop-off indicator */}
                {dropOff !== null && dropOff > 0 && entry.status !== 'cancelled' && (
                  <div className="flex items-center gap-2 ml-4 mb-1">
                    <div className="w-px h-3 bg-bg-tertiary" />
                    <span className="text-sm text-text-muted">
                      -{dropOff} ({((dropOff / (prevEntry?.count || 1)) * 100).toFixed(0)}% drop)
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  {/* Status label */}
                  <div className="w-28 flex-shrink-0">
                    <span
                      className={`text-base font-medium ${STATUS_TEXT_COLORS[entry.status]}`}
                    >
                      {STATUS_LABELS[entry.status]}
                    </span>
                  </div>

                  {/* Bar */}
                  <div className="flex-1">
                    <div className="w-full bg-bg-elevated rounded h-6 relative overflow-hidden">
                      <div
                        className={`${STATUS_COLORS[entry.status]} rounded h-6 transition-all duration-500 flex items-center`}
                        style={{ width: `${Math.max(barPct, entry.count > 0 ? 3 : 0)}%` }}
                      />
                    </div>
                  </div>

                  {/* Count + percentage */}
                  <div className="w-24 flex-shrink-0 text-right">
                    <span className="text-base font-medium text-text-primary">
                      {entry.count}
                    </span>
                    <span className="text-sm text-text-muted ml-2">
                      ({entry.pctOfTotal}%)
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
